import { type FC, useState, useRef, useEffect, useId } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Input } from "~/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";
import { cn } from "~/shared/lib/utils";

export const ComboboxFilter: FC<{
    options: { value: string; label: string; className?: string }[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    allowCustomValue?: boolean;
    customValueHint?: string;
    noResultsText?: string;
    hasValue?: boolean;
}> = ({
    options,
    value,
    onChange,
    placeholder,
    allowCustomValue,
    customValueHint,
    noResultsText,
    hasValue,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 0);
        } else {
            setSearch("");
        }
    }, [open]);

    const handleCommitCustomValue = () => {
        if (allowCustomValue && search.trim()) {
            onChange(search.trim());
            setOpen(false);
        }
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && allowCustomValue && search.trim()) {
            onChange(search.trim());
        }
        setOpen(nextOpen);
        // Remove focus ring when popover closes
        if (!nextOpen) {
            setTimeout(() => {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
            }, 0);
        }
    };

    const filtered = options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));
    const selectedLabel = options.find((opt) => opt.value === value)?.label;

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    className={cn(
                        "flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-input bg-input/60 px-3 text-sm shadow-xs transition-[color,box-shadow,background-color] hover:bg-input/80 dark:bg-panel-background dark:hover:bg-input/50",
                        hasValue && "pr-7",
                    )}
                >
                    <span className="min-w-0 truncate">
                        {selectedLabel ??
                            (value && value !== "__all__" ? value : null) ??
                            placeholder ??
                            "Select..."}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                id={listboxId}
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
            >
                <div className="p-2">
                    <Input
                        ref={inputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleCommitCustomValue();
                            }
                        }}
                        placeholder={placeholder ?? "Search..."}
                        className="h-8"
                    />
                </div>
                <div className="max-h-[400px] overflow-y-auto px-1 pb-1">
                    {filtered.length === 0 && (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            {allowCustomValue && search.trim()
                                ? (customValueHint ?? "Press Enter to use custom value")
                                : (noResultsText ?? "No results")}
                        </div>
                    )}
                    {filtered.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={cn(
                                "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground",
                                value === opt.value && "bg-accent",
                            )}
                            onClick={() => {
                                onChange(opt.value);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    "size-4 shrink-0",
                                    value === opt.value ? "opacity-100" : "opacity-0",
                                )}
                            />
                            <span className="truncate">{opt.label}</span>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
};
